import { FC } from 'react'
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid'
import {
  BuildingOffice2Icon,
  ShieldCheckIcon,
  UserCircleIcon,
  UserIcon,
  UsersIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'

export type ConsensusRecommendationsType = {
  title: string
  text: string
  typeOfRecommendation: string
}

export const ConsensusRecommendations: FC = () => {
  return (
    <main className="min-h-screen bg-[#EDE6DE] px-4 py-12 md:px-8 lg:px-16">
      <div className="max-w-[1400px] mx-auto">
        <h1 className="text-4xl md:text-5xl font-serif text-[#3C2416] mb-12">
          Consensus Recommendations
        </h1>

        <section className="flex flex-col xl:flex-row gap-8">
          <div className="xl:w-1/2">
            <div className="prose prose-lg max-w-none">
              <h2 className="text-xl text-[#A6410A] font-medium mb-4">
                Background
              </h2>
              <p className="text-[#3C2416]">
                Guidelines for routine care and monitoring of patients with
                sickle cell disease (SCD) exist across several different
                organizations. In the United States, these include the{' '}
                <strong>American Academy of Pediatrics</strong> (AAP) Health
                Supervision for Children with SCD; the{' '}
                <strong>American College of Emergency Physicians</strong> (ACEP)
                SCD point of care tool; the{' '}
                <strong>American Journal of Nursing</strong> (AJN) evidence
                review of nursing assessments; the{' '}
                <strong>American Society of Hematology</strong> (ASH) Clinical
                Practice Guidelines; the{' '}
                <strong>
                  National Academies of Sciences Engineering and Medicine
                </strong>{' '}
                (NASEM) Strategic Plan and Blueprint for Action; the{' '}
                <strong>National Heart, Lung, and Blood Institute</strong>{' '}
                (NHLBI) Expert Panel Report; and the{' '}
                <strong>National Institutes of Health</strong> (NIH). In Europe,
                the UK (British Journal of Haematology), Italy (Italian
                Association of Pediatric Hematology-Oncology [AIEOP]), and
                France also have their own guidelines.{' '}
              </p>
            </div>
          </div>

          <div className="xl:w-1/2">
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="flex justify-between items-center p-6">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-8 h-8 text-[#A6410A]">
                    <ShieldCheckIcon className="w-6 h-6" />
                  </div>
                  <h2 className="text-xl text-[#A6410A] font-medium">
                    Continuing the Process
                  </h2>
                </div>
              </div>

              <div className="px-6 pb-6">
                <p className="text-[#3C2416]">
                  The <strong>National Alliance of Sickle Cell Centers</strong>{' '}
                  (NASCC) conducted the first consensus meeting in July 2022, to
                  develop consensus recommendations for SCD management in areas
                  where current guidelines do not exist, are outlined or
                  conflicting from different groups, or lack specificity and
                  rigor. As a part of that meeting, participants were sent
                  literature to review ahead of time and asked to complete a
                  survey to assess their center&#39;s current SCD management.
                  First topics are presented here as tables for recommendations
                  for routine screening and assessments for people living with
                  sickle cell disease. These recommendations are reviewed every
                  year and are current as of our 2024 Annual Consensus
                  Conference.
                </p>
              </div>
            </div>
          </div>
        </section>
        <section>
          <div className="pt-6">
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="flex justify-between items-center p-6">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-8 h-8 text-[#A6410A]">
                    <MagnifyingGlassIcon className="w-6 h-6" />
                  </div>
                  <h2 className="text-xl text-[#A6410A] font-medium">
                    See the Recommendations
                  </h2>
                </div>
              </div>

              <div className="px-6 pb-6">
                <p className="text-[#3C2416]">
                  Click the link below to view a searchable table of NASCC&#39;s
                  recommendations.{' '}
                  <strong>
                    There is also a link on each page to download the
                    information in PDF form.
                  </strong>
                </p>
                <br />
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6 mt-2">
                  <Link
                    to="/consensus-recommendations/adult"
                    className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-all duration-200 border border-gray-100 hover:border-[#A6410A] group"
                  >
                    <div className="mb-4 flex items-center justify-center w-16 h-16 rounded-full bg-[#FDF7F2] text-[#A6410A]">
                      <UserIcon className="w-8 h-8 group-hover:scale-110 transition-transform duration-200" />
                    </div>
                    <h3 className="text-[#A6410A] text-xl font-semibold mb-3">
                      Adult
                    </h3>
                    <p className="text-[#3C2416] leading-relaxed">
                      Care Delivery and Health Maintenance Consensus
                      Recommendations for Individuals Over 18 Years Old
                    </p>
                  </Link>

                  <Link
                    to="/consensus-recommendations/pediatric"
                    className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-all duration-200 border border-gray-100 hover:border-[#A6410A] group"
                  >
                    <div className="mb-4 flex items-center justify-center w-16 h-16 rounded-full bg-[#FDF7F2] text-[#A6410A]">
                      <UsersIcon className="w-8 h-8 group-hover:scale-110 transition-transform duration-200" />
                    </div>
                    <h3 className="text-[#A6410A] text-xl font-semibold mb-3">
                      Pediatric
                    </h3>
                    <p className="text-[#3C2416] leading-relaxed">
                      Care Delivery and Health Maintenance Consensus
                      Recommendations for Pediatric Patients
                    </p>
                  </Link>

                  <Link
                    to="/consensus-recommendations/acute-pain"
                    className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-all duration-200 border border-gray-100 hover:border-[#A6410A] group"
                  >
                    <div className="mb-4 flex items-center justify-center w-16 h-16 rounded-full bg-[#FDF7F2] text-[#A6410A]">
                      <ExclamationTriangleIcon className="w-8 h-8 group-hover:scale-110 transition-transform duration-200" />
                    </div>
                    <h3 className="text-[#A6410A] text-xl font-semibold mb-3">
                      Acute Pain Crisis
                    </h3>
                    <p className="text-[#3C2416] leading-relaxed">
                      Recommendations for the Management of Uncomplicated Acute
                      Pain Crisis in SCD
                    </p>
                  </Link>

                  <Link
                    to="/consensus-recommendations/infant"
                    className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-all duration-200 border border-gray-100 hover:border-[#A6410A] group"
                  >
                    <div className="mb-4 flex items-center justify-center w-16 h-16 rounded-full bg-[#FDF7F2] text-[#A6410A]">
                      <UserCircleIcon className="w-8 h-8 group-hover:scale-110 transition-transform duration-200" />
                    </div>
                    <h3 className="text-[#A6410A] text-xl font-semibold mb-3">
                      Infant
                    </h3>
                    <p className="text-[#3C2416] leading-relaxed">
                      Care Delivery and Health Maintenance Consensus
                      Recommendations for 0 - 24 months old
                    </p>
                  </Link>

                  <Link
                    to="/consensus-recommendations/transformative-scd-center"
                    className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-all duration-200 border border-gray-100 hover:border-[#A6410A] group"
                  >
                    <div className="mb-4 flex items-center justify-center w-16 h-16 rounded-full bg-[#FDF7F2] text-[#A6410A]">
                      <BuildingOffice2Icon className="w-8 h-8 group-hover:scale-110 transition-transform duration-200" />
                    </div>
                    <h3 className="text-[#A6410A] text-xl font-semibold mb-3">
                      Transformative SCD Center
                    </h3>
                    <p className="text-[#3C2416] leading-relaxed">
                      Care Delivery and Health Maintenance Consensus
                      Recommendations for Transformative SCD Centers
                    </p>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  )
}
